import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, catchError, debounceTime, distinctUntilChanged, finalize, forkJoin, of, switchMap, take } from 'rxjs';
import { CurrentUserService } from '../../core/auth/current-user.service';
import { Business, BusinessType } from '../../core/models/user.models';
import { BusinessService } from '../../core/services/business.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { LocationService, LocationSuggestion } from '../../core/services/location.service';

@Component({
  selector: 'app-business-onboarding',
  imports: [FormsModule],
  templateUrl: './business-onboarding.html',
  styleUrl: './business-onboarding.scss',
})
export class BusinessOnboarding implements OnInit {
  private readonly businesses = inject(BusinessService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly locations = inject(LocationService);
  private readonly addressTerms = new Subject<string>();

  businessTypes: BusinessType[] = [];
  isLoading = true;
  isSubmitting = false;
  errorMessage = '';
  hasSubmittedProfile = false;
  approvalStatus = 'Pending';
  approvalReason = '';
  isCorrectionMode = false;
  termsAccepted = false;
  fieldErrors: Record<string, string> = {};
  addressSearch = '';
  addressSuggestions: LocationSuggestion[] = [];
  selectedAddress = '';
  isAddressSearching = false;
  isAddressResolving = false;
  addressError = '';
  readonly documentSlots = [
    { type: 'BUSINESS_REGISTRATION', name: 'Business registration certificate', number: '', file: null as File | null, preview: '', existingUrl: '' },
    { type: 'PAN', name: 'Business PAN card', number: '', file: null as File | null, preview: '', existingUrl: '' },
    { type: 'OWNER_ID', name: 'Owner / authorised person identity proof', number: '', file: null as File | null, preview: '', existingUrl: '' },
    { type: 'ADDRESS_PROOF', name: 'Registered address proof', number: '', file: null as File | null, preview: '', existingUrl: '' },
    { type: 'BANK_PROOF', name: 'Cancelled cheque / bank proof', number: '', file: null as File | null, preview: '', existingUrl: '' },
  ];

  form = {
    businessTypeId: '',
    legalName: '',
    tradingName: '',
    registrationNumber: '',
    taxNumber: '',
    email: '',
    phone: '',
    website: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    bankName: '',
    branchName: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
  };

  ngOnInit(): void {
    const user = this.currentUser.user();
    this.form.email = user?.email ?? '';
    this.form.phone = (user?.phone ?? '').replace(/\D/g, '').slice(-10);
    this.form.accountHolderName = user?.name ?? '';

    this.addressTerms.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      switchMap((input) => {
        this.isAddressSearching = true;
        this.addressError = '';
        this.cdr.detectChanges();
        return this.locations.autocomplete(input).pipe(
          catchError((error: unknown) => {
            this.addressError = httpErrorMessage(error, 'Unable to search Google addresses.');
            return of([] as LocationSuggestion[]);
          }),
          finalize(() => {
            this.isAddressSearching = false;
            this.cdr.detectChanges();
          }),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((suggestions) => {
      this.addressSuggestions = suggestions;
      this.cdr.detectChanges();
    });

    this.loadOnboarding();
  }

  loadOnboarding(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.cdr.detectChanges();
    forkJoin({
      profile: this.businesses.mine(),
      types: this.businesses.types(),
      registration: this.businesses.myApprovalStatus(),
    })
      .pipe(
        take(1),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: ({ profile, types, registration }) => {
          this.isLoading = false;
          this.businessTypes = types;
          if (!this.form.phone && registration.phone) {
            this.form.phone = registration.phone.replace(/\D/g, '').slice(-10);
          }
          if (profile) {
            this.approvalStatus = registration.approvalStatus;
            this.approvalReason = registration.approvalReason ?? '';
            if (registration.approvalStatus === 'CorrectionRequested') {
              this.isCorrectionMode = true;
              this.hasSubmittedProfile = false;
              this.populateCorrectionForm(profile);
              this.cdr.detectChanges();
              return;
            }
            this.hasSubmittedProfile = true;
            if (registration.approvalStatus === 'Approved') {
              void this.router.navigate(['/business/plan-selection']);
            }
            return;
          }
          this.businessTypes = types;
          this.form.businessTypeId = types[0]?.id ?? '';
          this.cdr.detectChanges();
        },
        error: (error: unknown) => {
          this.isLoading = false;
          this.errorMessage = httpErrorMessage(error, 'Unable to load business onboarding.');
          this.cdr.detectChanges();
        },
      });
  }

  submit(): void {
    if (this.isSubmitting) return;
    this.fieldErrors = {};
    this.normalizeWebsite();
    if (!this.form.businessTypeId) {
      this.fieldErrors['businessTypeId'] = 'Select a business type.';
    }
    if (this.form.legalName.trim().length < 2 || this.form.legalName.trim().length > 200) {
      this.fieldErrors['legalName'] = 'Legal business name must contain 2 to 200 characters.';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email.trim())) {
      this.fieldErrors['email'] = 'A valid verified business email is required.';
    }
    if (this.form.website && !this.isValidWebsite(this.form.website)) {
      this.fieldErrors['website'] = 'Enter a valid HTTPS website, for example https://example.com.';
    }
    if (!/^[6-9]\d{9}$/.test(this.form.phone)) {
      this.fieldErrors['phone'] = 'Enter a valid 10-digit Indian mobile number.';
    }
    if (!/^[1-9]\d{5}$/.test(this.form.postalCode)) {
      this.fieldErrors['postalCode'] = 'Enter a valid 6-digit PIN code.';
    }
    if (this.form.registrationNumber && !/^[LU]\d{5}[A-Z]{2}\d{4}[A-Z]{3}\d{6}$/.test(this.form.registrationNumber)) {
      this.fieldErrors['registrationNumber'] = 'Enter a valid 21-character CIN.';
    }
    if (this.form.taxNumber && !/^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(this.form.taxNumber)) {
      this.fieldErrors['taxNumber'] = 'Enter a valid 15-character GSTIN.';
    }
    if (!this.selectedAddress) {
      this.fieldErrors['address'] = 'Search and select the registered address from Google.';
    }
    if (!this.form.city.trim()) this.fieldErrors['city'] = 'City is required.';
    if (!this.form.state.trim()) this.fieldErrors['state'] = 'State is required.';
    if (this.form.bankName.trim().length < 2) this.fieldErrors['bankName'] = 'Enter the bank name.';
    if (this.form.accountHolderName.trim().length < 2) this.fieldErrors['accountHolderName'] = 'Enter the account-holder name.';
    if (!/^\d{9,18}$/.test(this.form.accountNumber)) {
      this.fieldErrors['accountNumber'] = 'Account number must contain 9 to 18 digits only.';
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(this.form.ifscCode)) {
      this.fieldErrors['ifscCode'] = 'Use a valid IFSC: 4 letters, 0, then 6 letters or digits.';
    }
    if (!this.termsAccepted) {
      this.fieldErrors['termsAccepted'] = 'Accept the Terms of Use and Privacy Policy to continue.';
    }
    if (Object.keys(this.fieldErrors).length) {
      this.errorMessage = 'Correct the highlighted fields before submitting.';
      this.cdr.detectChanges();
      return;
    }
    if (
      !this.form.bankName.trim() ||
      !this.form.accountHolderName.trim() ||
      !this.form.accountNumber.trim() ||
      !this.form.ifscCode.trim()
    ) {
      this.errorMessage = 'Complete the primary bank account details, including IFSC code.';
      return;
    }
    if (!this.isCorrectionMode && this.documentSlots.some((document) => !document.file)) {
      this.errorMessage = 'Upload all five required verification documents before submission.';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    const payload = new FormData();
    const fields: Record<string, string> = {
      businessTypeId: this.form.businessTypeId,
      legalName: this.form.legalName.trim(),
      tradingName: this.form.tradingName.trim(),
      registrationNumber: this.form.registrationNumber.trim(),
      taxNumber: this.form.taxNumber.trim(),
      email: this.form.email.trim(),
      phone: this.form.phone.trim(),
      website: this.form.website.trim(),
      addressLine1: this.form.addressLine1.trim(),
      addressLine2: this.form.addressLine2.trim(),
      city: this.form.city.trim(),
      state: this.form.state.trim(),
      country: 'India',
      postalCode: this.form.postalCode.trim(),
      termsAccepted: String(this.termsAccepted),
      termsVersion: 'business-onboarding-v1',
    };
    Object.entries(fields).forEach(([key, value]) => payload.append(key, value));
    const changedDocuments = this.documentSlots.filter((document) => document.file);
    payload.append('documentsJson', JSON.stringify(changedDocuments.map((document) => ({
      documentType: document.type,
      documentName: document.name,
      documentNumber: document.number.trim() || null,
    }))));
    payload.append('bankDetailJson', JSON.stringify({
      bankName: this.form.bankName.trim(), branchName: this.form.branchName.trim() || null,
      accountHolderName: this.form.accountHolderName.trim(), accountNumber: this.form.accountNumber.trim(),
      routingNumber: this.form.ifscCode.trim().toUpperCase(), swiftCode: null, iban: null,
      currency: 'INR', isPrimary: true,
    }));
    changedDocuments.forEach((document) => payload.append('documents', document.file!));
    this.businesses
      .onboardWithDocuments(payload)
      .pipe(
        take(1),
        finalize(() => {
          this.isSubmitting = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: () => {
          if (this.isCorrectionMode) {
            this.businesses.resubmitMine().subscribe({
              next: () => this.finishSubmission(),
              error: (error: unknown) => { this.isSubmitting = false; this.errorMessage = httpErrorMessage(error, 'Details were saved, but resubmission could not be completed. Please try again.'); this.cdr.detectChanges(); },
            });
          } else this.finishSubmission();
        },
        error: (error: unknown) => {
          this.isSubmitting = false;
          this.errorMessage = httpErrorMessage(error, 'Unable to complete business onboarding.');
          this.cdr.detectChanges();
        },
      });
  }

  selectDocument(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    const slot = this.documentSlots[index];
    if (slot.preview) URL.revokeObjectURL(slot.preview);
    slot.file = file;
    slot.preview = file ? URL.createObjectURL(file) : '';
  }

  removeDocument(index: number): void {
    const slot = this.documentSlots[index];
    if (slot.preview) URL.revokeObjectURL(slot.preview);
    slot.file = null;
    slot.preview = '';
  }

  sanitizePhone(): void {
    this.form.phone = this.form.phone.replace(/\D/g, '').slice(0, 10);
    delete this.fieldErrors['phone'];
  }

  sanitizePostalCode(): void {
    this.form.postalCode = this.form.postalCode.replace(/\D/g, '').slice(0, 6);
    delete this.fieldErrors['postalCode'];
  }

  sanitizeAccountNumber(): void {
    this.form.accountNumber = this.form.accountNumber.replace(/\D/g, '').slice(0, 18);
    delete this.fieldErrors['accountNumber'];
  }

  sanitizeIfsc(): void {
    this.form.ifscCode = this.form.ifscCode.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
    delete this.fieldErrors['ifscCode'];
  }

  sanitizeCin(): void {
    this.form.registrationNumber = this.form.registrationNumber.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 21);
    delete this.fieldErrors['registrationNumber'];
  }

  sanitizeGstin(): void {
    this.form.taxNumber = this.form.taxNumber.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);
    delete this.fieldErrors['taxNumber'];
  }

  normalizeWebsite(): void {
    const value = this.form.website.trim();
    if (!value) return;
    this.form.website = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    delete this.fieldErrors['website'];
  }

  private finishSubmission(): void {
    this.isSubmitting = false; this.isCorrectionMode = false; this.hasSubmittedProfile = true;
    this.approvalStatus = 'Pending'; this.approvalReason = ''; this.errorMessage = ''; this.cdr.detectChanges();
  }

  private populateCorrectionForm(profile: Business): void {
    this.form = {
      businessTypeId: profile.businessTypeId, legalName: profile.legalName, tradingName: profile.tradingName ?? '',
      registrationNumber: profile.registrationNumber ?? '', taxNumber: profile.taxNumber ?? '', email: profile.email ?? this.form.email,
      phone: (profile.phone ?? this.form.phone).replace(/\D/g, '').slice(-10), website: profile.website ?? '',
      addressLine1: profile.addressLine1 ?? '', addressLine2: profile.addressLine2 ?? '', city: profile.city ?? '',
      state: profile.state ?? '', postalCode: profile.postalCode ?? '', bankName: profile.bankDetails?.[0]?.bankName ?? '',
      branchName: profile.bankDetails?.[0]?.branchName ?? '', accountHolderName: profile.bankDetails?.[0]?.accountHolderName ?? '',
      accountNumber: profile.bankDetails?.[0]?.accountNumber ?? '', ifscCode: profile.bankDetails?.[0]?.routingNumber ?? '',
    };
    this.selectedAddress = profile.addressLine1 ?? '';
    this.addressSearch = profile.addressLine1 ?? '';
    for (const slot of this.documentSlots) {
      const existing = profile.documents?.find((document) => document.documentType === slot.type);
      slot.existingUrl = existing?.fileUrl ?? ''; slot.number = existing?.documentNumber ?? '';
    }
  }

  searchAddress(value: string): void {
    this.addressSearch = value;
    this.selectedAddress = '';
    delete this.fieldErrors['address'];
    const query = value.trim();
    if (query.length < 3) {
      this.addressSuggestions = [];
      return;
    }
    this.addressTerms.next(query);
  }

  selectAddress(suggestion: LocationSuggestion): void {
    this.addressSuggestions = [];
    this.addressSearch = suggestion.description;
    this.isAddressResolving = true;
    this.addressError = '';
    this.locations.placeDetails(suggestion.placeId).pipe(
      finalize(() => {
        this.isAddressResolving = false;
        this.cdr.detectChanges();
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (location) => {
        this.selectedAddress = location.formattedAddress;
        this.form.addressLine1 = location.formattedAddress;
        this.form.city = location.city ?? '';
        this.form.state = location.state ?? '';
        this.form.postalCode = (location.postalCode ?? location.zipCode ?? '').replace(/\D/g, '').slice(0, 6);
        delete this.fieldErrors['address'];
        delete this.fieldErrors['postalCode'];
        this.cdr.detectChanges();
      },
      error: (error: unknown) => {
        this.addressError = httpErrorMessage(error, 'Unable to load the selected Google address.');
        this.cdr.detectChanges();
      },
    });
  }

  private isValidWebsite(value: string): boolean {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && url.hostname.includes('.');
    } catch {
      return false;
    }
  }
}
